/**
 * V165.6 — Phase 3 Inventory Agent Tab
 *
 * Shows the user a live inventory health snapshot:
 *  • Counts: out-of-stock, low-stock, slow movers, reorder suggestions
 *  • Detailed lists for each alert category (top 50 each)
 *  • Dismiss-for-24h button on each alert
 *  • Activation CTA if agent not yet started
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Boxes, AlertOctagon, AlertTriangle, Clock, TrendingDown,
  Loader2, Sparkles, ExternalLink, X, Bell, CheckCircle, ChevronDown,
} from "lucide-react";
import { API } from "../../config";

export default function InventoryAgentTab({ user, t = {} }) {
  const email = (user?.email || "").toLowerCase();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("out_of_stock");
  const [starting, setStarting] = useState(false);
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookStatus, setWebhookStatus] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/inventory-agent/snapshot`, {
        params: { email }, withCredentials: true,
      });
      setSnap(r.data);
    } catch {/* ignore */}
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (email) load();
    // eslint-disable-next-line
  }, [email]);

  useEffect(() => {
    if (snap?.agent?.slack_webhook_url) {
      setWebhookUrl(snap.agent.slack_webhook_url);
    }
  }, [snap?.agent?.slack_webhook_url]);

  const saveWebhook = async () => {
    setWebhookStatus("saving");
    try {
      await axios.post(`${API}/inventory-agent/configure-webhook`, {
        email, slack_webhook_url: webhookUrl,
      }, { withCredentials: true });
      setWebhookStatus("saved");
      await load();
      setTimeout(() => setWebhookStatus(null), 2000);
    } catch (e) {
      setWebhookStatus("error:" + (e?.response?.data?.detail || "save_failed"));
    }
  };

  const testWebhook = async () => {
    setWebhookStatus("testing");
    try {
      const r = await axios.post(`${API}/inventory-agent/test-webhook`, {
        email, slack_webhook_url: webhookUrl,
      }, { withCredentials: true });
      setWebhookStatus(r.data?.ok ? "test-ok" : "test-failed");
      setTimeout(() => setWebhookStatus(null), 3000);
    } catch (e) {
      setWebhookStatus("error:" + (e?.response?.data?.detail || "test_failed"));
    }
  };

  const startAgent = async () => {
    setStarting(true);
    try {
      await axios.post(`${API}/inventory-agent/start`, {
        email, low_stock_threshold: 5, slow_mover_days: 90,
        enable_reorder_predictions: true,
      }, { withCredentials: true });
      await load();
    } catch {/* ignore */}
    finally { setStarting(false); }
  };

  const dismiss = async (sku) => {
    try {
      await axios.post(`${API}/inventory-agent/dismiss-alert`, { email, sku }, { withCredentials: true });
      await load();
    } catch {/* ignore */}
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-gray-500 p-6" data-testid="inv-loading">
        <Loader2 className="w-5 h-5 animate-spin" /><span>{t.inv_loading || "Načítavam zásoby…"}</span>
      </div>
    );
  }

  if (!snap?.active) {
    return (
      <div className="p-8 text-center bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 rounded-xl" data-testid="inv-empty">
        <Boxes className="w-12 h-12 text-orange-600 mx-auto mb-4" />
        <h2 className="text-xl font-black text-gray-800 mb-2 font-['Chivo']">
          {t.inv_empty_h || "Inventory Agent nie je aktívny"}
        </h2>
        <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
          {t.inv_empty_sub || "Aktivujte agenta — sleduje zásoby naprieč vašimi e-shopmi a upozorní vás na nízky stav, pomalých predajcov a doplnenie zásob."}
        </p>
        <button
          onClick={startAgent}
          disabled={starting}
          className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-3 rounded-lg disabled:opacity-60"
          data-testid="inv-activate"
        >
          {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {t.inv_activate || "Aktivovať Inventory Agenta"}
        </button>
      </div>
    );
  }

  const totals = snap.totals || {};
  const noProducts = !totals.products;

  if (noProducts) {
    return (
      <div className="p-8 bg-amber-50 border border-amber-200 rounded-lg text-center" data-testid="inv-no-products">
        <AlertTriangle className="w-8 h-8 text-amber-600 mx-auto mb-3" />
        <h3 className="font-bold text-amber-900 mb-2">{t.inv_no_products_h || "Žiadne produkty sa nesynchronizovali"}</h3>
        <p className="text-sm text-amber-700 mb-4">{snap.hint || t.inv_no_products_sub || "Pripojte e-shop v sekcii Integrácie."}</p>
        <a href="/dashboard?tab=eshop" className="text-amber-700 font-bold underline">{t.inv_connect || "Pripojiť e-shop →"}</a>
      </div>
    );
  }

  const TAB_DATA = {
    out_of_stock: { items: snap.out_of_stock || [], label: t.inv_oos || "Vypredané", icon: AlertOctagon, color: "rose", count: totals.out_of_stock || 0 },
    low_stock:    { items: snap.low_stock || [], label: t.inv_low || "Nízky stav", icon: AlertTriangle, color: "amber", count: totals.low_stock || 0 },
    slow_movers:  { items: snap.slow_movers || [], label: t.inv_slow || "Pomalé pohyby", icon: Clock, color: "blue", count: totals.slow_movers || 0 },
    reorder:      { items: snap.reorder_suggestions || [], label: t.inv_reorder || "Doplniť zásoby", icon: TrendingDown, color: "purple", count: totals.reorder_suggestions || 0 },
  };

  const active = TAB_DATA[tab] || TAB_DATA.out_of_stock;
  const webhookConfigured = !!snap?.agent?.slack_webhook_url;

  return (
    <div className="space-y-5" data-testid="inv-dashboard">
      {/* Webhook config panel */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="inv-webhook-panel">
        <button
          onClick={() => setWebhookOpen(!webhookOpen)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50"
          data-testid="inv-webhook-toggle"
        >
          <div className="flex items-center gap-3">
            <Bell className={`w-4 h-4 ${webhookConfigured ? "text-emerald-600" : "text-gray-400"}`} />
            <span className="text-sm font-bold text-gray-800">
              {t.inv_webhook || "Slack / Discord alerts"}
            </span>
            {webhookConfigured ? (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 flex items-center gap-1">
                <CheckCircle className="w-2.5 h-2.5" /> Connected
              </span>
            ) : (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                Off
              </span>
            )}
          </div>
          <ChevronIcon open={webhookOpen} />
        </button>
        {webhookOpen && (
          <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
            <p className="text-xs text-gray-500">
              {t.inv_webhook_hint || "Pri každom novom out-of-stock SKU pošleme ping do tvojho Slack/Discord kanálu (1× za 24h per SKU)."}
            </p>
            <input
              type="text"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://hooks.slack.com/services/... or https://discord.com/api/webhooks/..."
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono"
              data-testid="inv-webhook-input"
            />
            <div className="flex gap-2">
              <button
                onClick={saveWebhook}
                disabled={webhookStatus === "saving"}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded disabled:opacity-60"
                data-testid="inv-webhook-save"
              >
                {webhookStatus === "saving" ? "..." : (t.inv_webhook_save || "Save")}
              </button>
              <button
                onClick={testWebhook}
                disabled={!webhookUrl || webhookStatus === "testing"}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-bold px-4 py-2 rounded disabled:opacity-60"
                data-testid="inv-webhook-test"
              >
                {webhookStatus === "testing" ? "..." : (t.inv_webhook_test || "Send test ping")}
              </button>
              {webhookStatus === "saved" && (
                <span className="text-xs text-emerald-600 self-center">✓ Saved</span>
              )}
              {webhookStatus === "test-ok" && (
                <span className="text-xs text-emerald-600 self-center">✓ Test sent</span>
              )}
              {webhookStatus === "test-failed" && (
                <span className="text-xs text-rose-600 self-center">✗ Test failed</span>
              )}
              {(webhookStatus || "").startsWith("error:") && (
                <span className="text-xs text-rose-600 self-center">✗ {webhookStatus.slice(6)}</span>
              )}
            </div>
            <p className="text-[10px] text-gray-400">
              💡 Slack: Apps → Incoming Webhooks → Add → Copy URL. Discord: Server Settings → Integrations → Webhooks → New Webhook → Copy URL.
            </p>
          </div>
        )}
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="inv-counts">
        {Object.entries(TAB_DATA).map(([key, d]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`p-4 rounded-lg border text-left transition-all ${
              tab === key
                ? `bg-${d.color}-50 border-${d.color}-300`
                : "bg-white border-gray-200 hover:border-gray-300"
            }`}
            data-testid={`inv-count-${key}`}
          >
            <d.icon className={`w-5 h-5 text-${d.color}-600 mb-2`} />
            <p className="text-2xl font-black text-gray-800 font-['Chivo']">{d.count}</p>
            <p className="text-xs text-gray-500 mt-1">{d.label}</p>
          </button>
        ))}
      </div>

      {/* Active list */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <active.icon className={`w-4 h-4 text-${active.color}-600`} />
          <h3 className="text-sm font-bold text-gray-800">{active.label}</h3>
          <span className="text-xs text-gray-400 ml-auto">{active.items.length} {t.inv_shown || "shown"}</span>
        </div>
        {active.items.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">{t.inv_no_alerts || "Žiadne upozornenia tu — výborná práca!"}</p>
        ) : (
          <ul className="divide-y divide-gray-100" data-testid="inv-items">
            {active.items.map((p, i) => (
              <li key={p.sku || i} className="p-3 flex items-center gap-3 hover:bg-gray-50">
                {p.image_url ? (
                  <img src={p.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                ) : (
                  <div className="w-10 h-10 bg-gray-100 rounded" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-800 truncate">{p.title || p.sku}</p>
                  <p className="text-xs text-gray-500 truncate font-['JetBrains_Mono']">
                    {p.sku} {p.shop_name && `· ${p.shop_name}`}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-black text-gray-800">{p.stock ?? 0}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">{t.inv_in_stock || "v sklade"}</p>
                  {p.days_left != null && (
                    <p className="text-[10px] text-purple-600 font-bold mt-0.5">{p.days_left}d {t.inv_left || "left"}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  {p.url && (
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-400 hover:text-gray-700">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  <button onClick={() => dismiss(p.sku)} className="p-1.5 text-gray-400 hover:text-rose-600" data-testid={`inv-dismiss-${p.sku}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ChevronIcon({ open }) {
  return <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />;
}

